/** Max longest edge for library upload thumbnails (images and video). */
export const MEDIA_UPLOAD_THUMBNAIL_MAX_EDGE_PX = 480;

export const MEDIA_UPLOAD_IMAGE_THUMBNAIL_CONTENT_TYPE = 'image/jpeg';
export const MEDIA_UPLOAD_VIDEO_THUMBNAIL_CONTENT_TYPE = 'video/mp4';

const IMAGE_MIME_PREFIX = 'image/';
const VIDEO_MIME_PREFIX = 'video/';

const IMAGE_EXTENSIONS = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.gif',
]);

const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.webm', '.m4v']);

export type MediaUploadThumbnailResult = {
  blob: Blob;
  contentType: string;
};

export function getFileExtensionLower(name: string): string {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0) return '';
  return name.slice(lastDot).toLowerCase();
}

export function isImageMediaFile(file: File): boolean {
  if (file.type.startsWith(IMAGE_MIME_PREFIX)) return true;
  return IMAGE_EXTENSIONS.has(getFileExtensionLower(file.name));
}

export function isVideoMediaFile(file: File): boolean {
  if (file.type.startsWith(VIDEO_MIME_PREFIX)) return true;
  return VIDEO_EXTENSIONS.has(getFileExtensionLower(file.name));
}

export function shouldGenerateMediaUploadThumbnail(file: File): boolean {
  return isImageMediaFile(file) || isVideoMediaFile(file);
}

export function getCappedThumbnailSize(
  width: number,
  height: number,
  maxEdgePx: number = MEDIA_UPLOAD_THUMBNAIL_MAX_EDGE_PX,
): { width: number; height: number } {
  const normalizedWidth = Math.max(1, Math.round(width));
  const normalizedHeight = Math.max(1, Math.round(height));
  const maxCurrentEdge = Math.max(normalizedWidth, normalizedHeight);

  if (maxCurrentEdge <= maxEdgePx) {
    return { width: normalizedWidth, height: normalizedHeight };
  }

  const scale = maxEdgePx / maxCurrentEdge;
  return {
    width: Math.max(1, Math.round(normalizedWidth * scale)),
    height: Math.max(1, Math.round(normalizedHeight * scale)),
  };
}

/** H.264 encoders require even dimensions. */
export function ensureEvenDimension(value: number): number {
  const rounded = Math.max(2, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded - 1;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      mimeType,
      quality,
    );
  });
}

export async function generateImageUploadThumbnail(
  file: File,
): Promise<MediaUploadThumbnailResult> {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = getCappedThumbnailSize(
      bitmap.width,
      bitmap.height,
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('No 2d canvas context');
    }

    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(
      canvas,
      MEDIA_UPLOAD_IMAGE_THUMBNAIL_CONTENT_TYPE,
      0.82,
    );

    return {
      blob,
      contentType: MEDIA_UPLOAD_IMAGE_THUMBNAIL_CONTENT_TYPE,
    };
  } finally {
    bitmap.close();
  }
}

export async function uploadBlobToPresignedUrl(
  url: string,
  body: Blob | File,
  contentType: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': contentType },
    signal,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.statusText}`);
  }
}
